import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  Calendar,
  Save,
  Users,
  Loader2,
  Filter,
  Search,
  CheckSquare,
  Square,
} from 'lucide-react';
import {
  useTodayAttendance,
  useCheckIn,
  useCheckOut,
  useMarkPresent,
  useMarkAbsent,
  useBulkCheckIn,
} from '../../hooks/useAttendance';
import { useClasses } from '../../hooks/useClasses';
import { useStudents } from '../../hooks/useStudents';
import { StudentAttendanceCard, CheckInModal } from '../../components/attendance';

/**
 * ClassAttendance - Take attendance for a specific class
 */
const ClassAttendance = () => {
  const { tenantId, classId } = useParams();
  const navigate = useNavigate();

  // State
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [selectedClass, setSelectedClass] = useState(classId !== 'all' ? classId : '');
  const [searchQuery, setSearchQuery] = useState('');
  const [studentStatuses, setStudentStatuses] = useState({});
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [selectedStudentForCheckIn, setSelectedStudentForCheckIn] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch data
  const { data: classesData, isLoading: classesLoading } = useClasses();
  const { data: studentsData, isLoading: studentsLoading } = useStudents();
  const { data: attendanceData, isLoading: attendanceLoading } = useTodayAttendance(
    selectedClass
  );

  // Mutations
  const checkInMutation = useCheckIn();
  const checkOutMutation = useCheckOut();
  const markPresentMutation = useMarkPresent();
  const markAbsentMutation = useMarkAbsent();
  const bulkCheckInMutation = useBulkCheckIn();

  // Extract data - wrapped in useMemo to avoid recreating references on every render
  const classes = useMemo(() => classesData?.classes || [], [classesData?.classes]);
  const allStudents = useMemo(() => studentsData?.students || [], [studentsData?.students]);
  const todayAttendance = useMemo(() => attendanceData?.attendance || [], [attendanceData?.attendance]);

  // Get current class info
  const currentClass = useMemo(() => {
    return classes.find((c) => c.id === selectedClass);
  }, [classes, selectedClass]);

  // Get students for selected class
  const classStudents = useMemo(() => {
    if (!selectedClass) return [];
    return allStudents.filter((student) => {
      // Check if student is enrolled in this class
      return student.enrollments?.some((e) => e.classId === selectedClass);
    });
  }, [allStudents, selectedClass]);

  // Filter students by search
  const filteredStudents = useMemo(() => {
    if (!searchQuery) return classStudents;
    return classStudents.filter((student) => {
      const fullName = `${student.firstName} ${student.lastName}`.toLowerCase();
      return fullName.includes(searchQuery.toLowerCase());
    });
  }, [classStudents, searchQuery]);

  // Initialize student statuses from existing attendance
  useEffect(() => {
    if (todayAttendance.length > 0) {
      const statuses = {};
      todayAttendance.forEach((record) => {
        statuses[record.studentId] = {
          status: record.status,
          checkInTime: record.checkInTime,
          checkOutTime: record.checkOutTime,
          notes: record.notes,
          id: record.id,
        };
      });
      setStudentStatuses(statuses);
    } else {
      setStudentStatuses({});
    }
  }, [todayAttendance]);

  // Get attendance status for a student
  const getStudentAttendance = (studentId) => {
    return studentStatuses[studentId] || { status: 'not_marked' };
  };

  // Stats
  const stats = useMemo(() => {
    const total = classStudents.length;
    const present = Object.values(studentStatuses).filter(
      (s) => s.status === 'present'
    ).length;
    const late = Object.values(studentStatuses).filter(
      (s) => s.status === 'late'
    ).length;
    const absent = Object.values(studentStatuses).filter(
      (s) => s.status === 'absent'
    ).length;
    const notMarked = total - present - late - absent;

    return { total, present, late, absent, notMarked };
  }, [classStudents.length, studentStatuses]);

  // Handlers
  const handleClassChange = (classId) => {
    setSelectedClass(classId);
    if (classId) {
      navigate(`/${tenantId}/dashboard/attendance/class/${classId}`);
    } else {
      navigate(`/${tenantId}/dashboard/attendance/class/all`);
    }
  };

  const handleCheckIn = async (formData) => {
    try {
      await checkInMutation.mutateAsync({
        studentId: formData.studentId,
        classId: selectedClass,
        checkInTime: formData.checkInTime,
        method: formData.method,
        notes: formData.notes,
      });
      setShowCheckInModal(false);
      setSelectedStudentForCheckIn(null);
    } catch (error) {
      console.error('Check-in failed:', error);
      alert('Failed to check in: ' + error.message);
    }
  };

  const handleCheckOut = async (student) => {
    try {
      await checkOutMutation.mutateAsync({
        studentId: student.id,
        classId: selectedClass,
      });
    } catch (error) {
      console.error('Check-out failed:', error);
      alert('Failed to check out: ' + error.message);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const _handleMarkPresent = async (student) => {
    try {
      await markPresentMutation.mutateAsync({
        studentId: student.id,
        classId: selectedClass,
        date: selectedDate,
      });
    } catch (error) {
      console.error('Failed to mark present:', error);
      alert('Failed to mark present: ' + error.message);
    }
  };

  const handleMarkAbsent = async (student) => {
    try {
      await markAbsentMutation.mutateAsync({
        studentId: student.id,
        classId: selectedClass,
        date: selectedDate,
      });
    } catch (error) {
      console.error('Failed to mark absent:', error);
      alert('Failed to mark absent: ' + error.message);
    }
  };

  const handleMarkLate = async (student) => {
    try {
      await checkInMutation.mutateAsync({
        studentId: student.id,
        classId: selectedClass,
        checkInTime: new Date().toISOString(),
        method: 'manual',
        notes: 'Marked as late',
      });
    } catch (error) {
      console.error('Failed to mark late:', error);
      alert('Failed to mark late: ' + error.message);
    }
  };

  const handleMarkAllPresent = async () => {
    const notMarkedStudents = classStudents.filter(
      (s) => !studentStatuses[s.id] || studentStatuses[s.id].status === 'not_marked'
    );

    if (notMarkedStudents.length === 0) {
      alert('All students are already marked');
      return;
    }

    if (
      !window.confirm(
        `Mark ${notMarkedStudents.length} students as present?`
      )
    ) {
      return;
    }

    try {
      // Bulk check in all not-marked students
      await bulkCheckInMutation.mutateAsync({
        classId: selectedClass,
        studentIds: notMarkedStudents.map((s) => s.id),
        checkInTime: new Date().toISOString(),
        method: 'bulk',
      });
    } catch (error) {
      console.error('Bulk mark present failed:', error);
      alert('Failed to mark all present: ' + error.message);
    }
  };

  const handleMarkAllAbsent = async () => {
    const notMarkedStudents = classStudents.filter(
      (s) => !studentStatuses[s.id] || studentStatuses[s.id].status === 'not_marked'
    );

    if (notMarkedStudents.length === 0) {
      alert('All students are already marked');
      return;
    }

    if (
      !window.confirm(
        `Mark ${notMarkedStudents.length} students as absent?`
      )
    ) {
      return;
    }

    try {
      // Mark each student as absent
      await Promise.all(
        notMarkedStudents.map((student) =>
          markAbsentMutation.mutateAsync({
            studentId: student.id,
            classId: selectedClass,
            date: selectedDate,
          })
        )
      );
    } catch (error) {
      console.error('Bulk mark absent failed:', error);
      alert('Failed to mark all absent: ' + error.message);
    }
  };

  const handleSaveAttendance = async () => {
    setIsSubmitting(true);
    try {
      // In a real implementation, this would save any pending changes
      // For now, we just show a success message
      alert('Attendance saved successfully!');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openCheckInModal = (student) => {
    setSelectedStudentForCheckIn(student);
    setShowCheckInModal(true);
  };

  // Loading state
  const isLoading = classesLoading || studentsLoading || attendanceLoading;

  return (
    <div className="animate-fade-in h-full flex flex-col">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center space-x-2 mb-4">
          <button
            onClick={() => navigate(`/${tenantId}/dashboard/attendance/dashboard`)}
            className="flex items-center text-gray-500 hover:text-gray-700"
          >
            <ChevronLeft size={20} />
            <span>Back to Dashboard</span>
          </button>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {currentClass ? currentClass.name : 'Class Attendance'}
            </h1>
            <p className="text-gray-500 mt-1">
              Take attendance for your class.
            </p>
          </div>

          {/* Save Button */}
          <button
            onClick={handleSaveAttendance}
            disabled={isSubmitting}
            className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
          >
            {isSubmitting ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <Save size={18} />
            )}
            <span>Save Attendance</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-col lg:flex-row gap-4">
        {/* Class Selector */}
        <div className="flex items-center space-x-2 bg-white px-4 py-2 rounded-lg border border-gray-200">
          <Filter size={18} className="text-gray-400" />
          <select
            value={selectedClass}
            onChange={(e) => handleClassChange(e.target.value)}
            className="outline-none text-gray-700 bg-transparent w-full sm:min-w-[200px]"
          >
            <option value="">Select a class...</option>
            {classes.map((cls) => (
              <option key={cls.id} value={cls.id}>
                {cls.name}
              </option>
            ))}
          </select>
        </div>

        {/* Date Picker */}
        <div className="flex items-center space-x-2 bg-white px-4 py-2 rounded-lg border border-gray-200">
          <Calendar size={18} className="text-gray-400" />
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="outline-none text-gray-700 bg-transparent"
          />
        </div>

        {/* Search */}
        <div className="flex items-center space-x-2 bg-white px-4 py-2 rounded-lg border border-gray-200 flex-1 max-w-md">
          <Search size={18} className="text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search students..."
            className="outline-none text-gray-700 bg-transparent w-full"
          />
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <p className="text-sm text-gray-500">Total</p>
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-green-100">
          <p className="text-sm text-gray-500">Present</p>
          <p className="text-2xl font-bold text-green-600">{stats.present}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-red-100">
          <p className="text-sm text-gray-500">Absent</p>
          <p className="text-2xl font-bold text-red-600">{stats.absent}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-yellow-100">
          <p className="text-sm text-gray-500">Late</p>
          <p className="text-2xl font-bold text-yellow-600">{stats.late}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <p className="text-sm text-gray-500">Not Marked</p>
          <p className="text-2xl font-bold text-gray-400">{stats.notMarked}</p>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedClass && (
        <div className="mb-6 flex flex-wrap gap-3">
          <button
            onClick={handleMarkAllPresent}
            disabled={markPresentMutation.isPending}
            className="flex items-center space-x-2 px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
          >
            <CheckSquare size={18} />
            <span>Mark All Present</span>
          </button>
          <button
            onClick={handleMarkAllAbsent}
            disabled={markAbsentMutation.isPending}
            className="flex items-center space-x-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
          >
            <Square size={18} />
            <span>Mark All Absent</span>
          </button>
        </div>
      )}

      {/* Student List */}
      <div className="flex-1">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-purple-600" size={32} />
          </div>
        ) : !selectedClass ? (
          <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-200">
            <Filter size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 mb-2">Please select a class</p>
            <p className="text-sm text-gray-400">
              Choose a class from the dropdown above to view students
            </p>
          </div>
        ) : filteredStudents.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-200">
            <Users size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 mb-2">No students found</p>
            <p className="text-sm text-gray-400">
              {searchQuery
                ? 'Try adjusting your search'
                : 'No students are enrolled in this class'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredStudents.map((student) => (
              <StudentAttendanceCard
                key={student.id}
                student={student}
                attendance={getStudentAttendance(student.id)}
                onCheckIn={() => openCheckInModal(student)}
                onCheckOut={() => handleCheckOut(student)}
                onMarkLate={() => handleMarkLate(student)}
                onMarkAbsent={() => handleMarkAbsent(student)}
                onViewDetails={(s) => console.log('View details:', s)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Check In Modal */}
      <CheckInModal
        isOpen={showCheckInModal}
        onClose={() => {
          setShowCheckInModal(false);
          setSelectedStudentForCheckIn(null);
        }}
        onSubmit={handleCheckIn}
        students={selectedStudentForCheckIn ? [selectedStudentForCheckIn] : classStudents}
        preSelectedStudent={selectedStudentForCheckIn}
        isLoading={checkInMutation.isPending}
      />
    </div>
  );
};

export default ClassAttendance;
